import { useEffect, useRef, useState } from "react";
import { getSubcontractors } from "services";
import { formatCurrency } from "../constants/sovSetupData";
import SearchableSelect from "Components/Common/SearchableSelect/SearchableSelect";
import useDebounce from "hooks/useDebounce";
import NoData from "Pages/Admin/InspectorAssign/components/NoData";
import Input from "Components/Common/Input/Input";
import Button from "Components/Common/Button/Button";

interface RawSubcontractor {
  id?: string | number;
  companyName?: string;
}

interface VendorOption {
  id: string;
  name: string;
  subtitle?: string;
}

interface SovRow {
  id: string | number;
  csiCode: string;
  description: string;
  scheduledValue: number;
  vendor: string;
  vendorName?: string;
  vendorReadOnly?: boolean;
  status: string;
  isIncludeBudgetBreakdown?: boolean;
  isDeleted?: boolean;
}

export interface AiaOption {
  id: string;
  code: string;
  label: string;
  name: string;
  colorHex?: string;
  isIncludeBudgetBreakdown?: boolean;
}

interface RowStatus {
  label: string;
  className: string;
}

interface SOVTableProps {
  projectId: string;
  rows: SovRow[];
  onSelectCsiCode?: any;
  onChangeField: (
    rowId: string | number,
    field: string,
    value: string | number,
  ) => void;
  onDelete: (rowId: string | number) => void;
  onMove: (rowId: string | number, direction: number) => void;
  onAddRow: () => void;
  onExportCsv: () => void;
  isExporting?: boolean;
  total: number;
  contractAmount?: number;
  isPublished?: boolean;
  aiaOptions?: AiaOption[];
  highlightVariance?: boolean;
  highlightMissingVendors?: boolean;
  sovStatus?: string;
  /** GC only: lets the vendor dropdown send the user to add a new subcontractor. */
  onAddVendor?: (rowId: string | number) => void;
}

function normalizeSubcontractor(option: RawSubcontractor): VendorOption {
  const id = String(option?.id);
  const name = option?.companyName ?? "";
  return {
    id: id || name,
    name,
  };
}

function getRowStatus(row: SovRow): RowStatus {
  if (row.status === "new") {
    return { label: "New", className: "new" };
  }

  if (
    !row.description.trim() ||
    row.scheduledValue <= 0 ||
    !row.vendor.trim()
  ) {
    return { label: "Incomplete", className: "warn" };
  }

  return { label: "Complete", className: "ok" };
}

export default function SOVTable({
  projectId,
  rows,
  onChangeField,
  onDelete,
  onMove,
  onAddRow,
  onExportCsv,
  isExporting = false,
  total,
  contractAmount,
  isPublished,
  aiaOptions = [],
  highlightVariance = false,
  highlightMissingVendors = false,
  sovStatus,
  onAddVendor,
}: SOVTableProps) {
  const PAGE_SIZE = 10;
  const [vendorOptions, setVendorOptions] = useState<VendorOption[]>([]);
  const [vendorLoading, setVendorLoading] = useState(false);
  const [vendorHasMore, setVendorHasMore] = useState(false);
  const [search, setSearch] = useState("");
  const [loadParams, setLoadParams] = useState({ search: "", page: 1 });
  const hasFetched = useRef(false);

  const debouncedSearch = useDebounce(search, 400);

  useEffect(() => {
    if (!hasFetched.current) return;
    setLoadParams({ search: debouncedSearch, page: 1 });
  }, [debouncedSearch]);

  useEffect(() => {
    if (!projectId || !hasFetched.current) return;

    const { search: s, page } = loadParams;
    let cancelled = false;

    const loadVendors = async () => {
      setVendorLoading(true);
      const { data, error }: { data: any; error: any } =
        await getSubcontractors(s.trim(), page, PAGE_SIZE);
      if (cancelled) return;
      if (!error) {
        const pagination = data?.pagination;
        const list: RawSubcontractor[] = Array.isArray(data?.data)
          ? data!.data
          : Array.isArray(data)
            ? data
            : [];
        const normalized = list
          .map(normalizeSubcontractor)
          .filter((option) => option.id && option.name);
        const updated =
          page === 1 ? normalized : [...vendorOptions, ...normalized];
        setVendorOptions(updated);
        setVendorHasMore(updated.length < pagination.total);
      }
      setVendorLoading(false);
    };

    loadVendors();
    return () => {
      cancelled = true;
    };
  }, [projectId, loadParams]);

  const handleDropdownOpen = () => {
    if (hasFetched.current) return;
    hasFetched.current = true;
    setLoadParams({ search: "", page: 1 });
  };

  return (
    <div className="sovTableCard">
      <div className="sovTableHeader">
        <h4>AIA G703 - Schedule of Values</h4>

        <div className="sovTableHeaderActions">
          {sovStatus === "active" && (
            <Button
              type="button"
              className="btn secondary small"
              onClick={onExportCsv}
              disabled={isExporting}
            >
              {isExporting ? (
                <>
                  <span className="btn-spinner" />
                  Exporting
                </>
              ) : (
                "⬇ Export CSV"
              )}
            </Button>
          )}
          {/* For future use */}
          {/* <Button
            type="button"
            className="btn secondary small"
            onClick={onAddRow}
            disabled={isPublished}
          >
            + Add Line Item
          </Button> */}
        </div>
      </div>

      <div className="table-responsive">
        <table className="sovTable">
          <thead>
            <tr>
              <th>
                A
                <br />
                <span className="tableSubTitle">Item #</span>
              </th>
              <th>CSI Code</th>
              <th>
                B
                <br />
                <span className="tableSubTitle">Description of Work</span>
              </th>
              <th className="num">
                C
                <br />
                <span className="tableSubTitle"> Scheduled Value</span>
              </th>
              <th className="num">% of Contract</th>
              <th>Subcontractor / Vendor</th>
              <th>Status</th>
              <th className="center">Actions</th>
            </tr>
          </thead>

          <tbody>
            {rows.length === 0 && (
              <tr>
                <td colSpan={8} className="sovEmptyState">
                  <NoData />
                </td>
              </tr>
            )}
            {rows
              .filter((row) => !row.isDeleted)
              .map((row, index) => {
                const status = getRowStatus(row);
                const pct =
                  contractAmount && contractAmount > 0
                    ? Math.round((row.scheduledValue / contractAmount) * 100)
                    : "0.000";

                return (
                  <tr key={row.id}>
                    <td className="static">{index + 1}</td>

                    <td className="code">
                      <span className="static">{row.csiCode}</span>
                    </td>

                    <td>
                      <Input
                        className="cellInput"
                        value={row.description}
                        onChange={(event) =>
                          onChangeField(
                            row.id,
                            "description",
                            event.target.value,
                          )
                        }
                        placeholder="Description of work..."
                        disabled={isPublished}
                      />
                    </td>

                    <td>
                      <Input
                        className={`cellInput num${highlightVariance ? " error-input" : ""}`}
                        type="number"
                        min="0"
                        step="0.01"
                        value={row.scheduledValue}
                        onFocus={(e) => e.target.select()}
                        onChange={(event) =>
                          onChangeField(
                            row.id,
                            "scheduledValue",
                            Number(event.target.value || 0),
                          )
                        }
                        disabled={isPublished}
                      />
                    </td>

                    <td className="static num muted">{pct}%</td>

                    <td>
                      <SearchableSelect
                        portal
                        id={`vendor-${row.id}`}
                        value={row.vendor || ""}
                        valueLabel={row.vendorName || ""}
                        className={
                          highlightMissingVendors && !row.vendor?.trim()
                            ? "error-input"
                            : ""
                        }
                        options={vendorOptions}
                        onCreateNew={
                          onAddVendor && (() => onAddVendor(row.id))
                        }
                        createNewLabel="+ Add new subcontractor"
                        getOptionValue={(v: VendorOption) => v.id}
                        getOptionLabel={(v: VendorOption) =>
                          v.subtitle ? `${v.name} (${v.subtitle})` : v.name
                        }
                        placeholder={
                          vendorLoading
                            ? "Loading subcontractors..."
                            : "Search subcontractor..."
                        }
                        disabled={
                          row.vendorReadOnly || vendorLoading || isPublished
                        }
                        onOpen={handleDropdownOpen}
                        loading={vendorLoading}
                        onSearch={(value: string) => setSearch(value)}
                        apiSearch
                        hasMore={vendorHasMore && !vendorLoading}
                        onLoadMore={() =>
                          setLoadParams((prev) => ({
                            ...prev,
                            page: prev.page + 1,
                          }))
                        }
                        onChange={(id: string, vendor: VendorOption | null) => {
                          onChangeField(row.id, "vendor", id);
                          onChangeField(
                            row.id,
                            "vendorName",
                            vendor ? vendor.name : "",
                          );
                        }}
                      />
                    </td>

                    <td className="static">
                      <span className={`rowFlag ${status.className}`}>
                        {status.label}
                      </span>
                    </td>

                    <td>
                      <div
                        className={`rowActions ${isPublished ? "disabled" : ""}`}
                      >
                        <Button
                          type="button"
                          className="rowBtn"
                          onClick={() => onMove(row.id, -1)}
                          disabled={isPublished}
                        >
                          ↑
                        </Button>

                        <Button
                          type="button"
                          className="rowBtn"
                          onClick={() => onMove(row.id, 1)}
                          disabled={isPublished}
                        >
                          ↓
                        </Button>

                        <Button
                          type="button"
                          className="rowBtn del"
                          onClick={() => onDelete(row.id)}
                          disabled={isPublished}
                        >
                          x
                        </Button>
                      </div>
                    </td>
                  </tr>
                );
              })}

            {rows.length > 0 && (
              <tr className="totalRow">
                <td colSpan={3} className="static totalLabel">
                  Total Scheduled Value
                </td>

                <td className="static num totalValue">
                  {formatCurrency(total)}
                </td>

                <td className="static num">
                  {contractAmount && contractAmount > 0
                    ? Math.round(total / contractAmount) * 100
                    : "0.000"}
                  %
                </td>

                <td colSpan={3} className="static"></td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
